import dayjs from 'dayjs';
import {
    FC,
    useEffect,
    useMemo,
    useState,
} from 'react';
import {
    Avatar,
    Button,
    Card,
    Collapse,
    Drawer,
    Empty,
    PageHeader,
    Row,
    Space,
    Spin,
    Tag,
    Typography,
} from 'antd';
import {
    ThunderboltOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { BsLightbulbFill } from 'react-icons/bs';
import { useRequestsContext } from '../../context/requests.ctx';
import { IOrderSearch, IRequest } from '../../../../interfaces/requests.interface';
import { getRequestSearch } from '../../../../api/requests.api';

const { Panel } = Collapse;
const { Title, Text } = Typography;

const OrderSearch: FC<{ request: IRequest | null }> = ({ request }) => {
    const { t } = useTranslation('small-fleet-orders');

    const [searchDetails, setSearchDetails] = useState<IOrderSearch[]>([]);
    const [total, setTotal] = useState<number>(0);
    const [loading, setLoading] = useState(false);

    const { setViewSearch } = useRequestsContext();

    const fetchOrderSearch = async (id: string) => {
        setLoading(true);
        getRequestSearch<{ total: number; data: IOrderSearch[] }>(id)
            .then((response) => {
                setSearchDetails(response.data);
                setTotal(response.total);
            })
            .catch((error) => {
                console.error(error);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    useEffect(() => {
        if (request?.bookingID) {
            fetchOrderSearch(request.bookingID);
        }
    }, [request?.bookingID]);

    const groupedByRound = useMemo(() => {
        const groups: Record<number, IOrderSearch[]> = {};
        searchDetails.forEach((item) => {
            if (!groups[item.round]) {
                groups[item.round] = [];
            }
            groups[item.round].push(item);
        });
        return Object.entries(groups)
            .map(([round, drivers]) => ({ round: Number(round), drivers }))
            .sort((a, b) => a.round - b.round);
    }, [searchDetails]);

    const getBatteryClass = (percentage: number) => {
        if (percentage <= 20) return 'var(--color-error)';
        if (percentage <= 50) return 'var(--color-yellow-2)';
        return 'var(--color-green-4)';
    };

    const formatDate = (dateStr: string) => dayjs(dateStr).format('DD MMM YYYY HH:mm');

    const renderStatusTag = (value: boolean | undefined, label: string) => {
        if (value === undefined) return null;
        return (
            <Tag
                icon={value ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                color={value ? 'var(--color-green-1)' : 'default'}
                style={{
                    color: 'var(--color-dark)',
                    fontSize: 12,
                }}
            >
                {label}
            </Tag>
        );
    };

    const renderRoundHeader = (round: number, driversCount: number) => (
        <Row justify="space-between" align="middle">
            <Text strong style={{ color: 'var(--color-purple)' }}>
                {t('SEARCH_ROUND', { round })}
            </Text>
            <Text type="secondary" style={{ color: 'var(--color-neutral-4)', fontSize: 12 }}>
                {t('SEARCH_DRIVERS_COUNT', { count: driversCount })}
            </Text>
        </Row>
    );

    const renderDriverItem = (driver: IOrderSearch) => (
        <Card
            key={driver._id}
            style={{ marginBottom: 16 }}
            bodyStyle={{ padding: '12px 16px' }}
        >
            <Space direction="vertical" size={12}>
                <Row justify="space-between" align="middle">
                    <Space size={8}>
                        <Avatar
                            src={(
                                <Text strong style={{ fontSize: 12, color: 'var(--color-white)' }}>
                                    {driver.driverName?.charAt(0)}
                                </Text>
                            )}
                            style={{
                                backgroundColor: 'var(--color-purple)',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                            size={28}
                        />
                        <Space direction="vertical" size={0}>
                            <Text strong>{driver.driverName}</Text>
                            <Text type="secondary" style={{ fontSize: 12 }}>{driver.driverEmail}</Text>
                        </Space>
                    </Space>
                    <Space
                        direction="horizontal"
                        size={4}
                        style={{
                            color: getBatteryClass(driver.metadata.batteryPercentage),
                            fontSize: 12,
                        }}
                    >
                        <ThunderboltOutlined style={{ verticalAlign: 'middle' }} />
                        {` ${driver.metadata.batteryPercentage}%`}
                    </Space>
                </Row>
                <Space size={4} style={{ flexWrap: 'wrap' }}>
                    {renderStatusTag(driver.metadata.wasNotified || false, t('SEARCH_NOTIFIED'))}
                    {renderStatusTag((driver.metadata.wasReceived || false), t('SEARCH_RECEIVED'))}
                    {renderStatusTag(driver.metadata.wasRead || false, t('SEARCH_READ'))}
                    {renderStatusTag(driver.metadata.wasAccepted || false, t('SEARCH_ACCEPTED'))}
                </Space>
                <Text type="secondary" style={{ fontSize: 12 }}>{formatDate(driver.createdAt)}</Text>
            </Space>
        </Card>
    );

    return (
        <Drawer
            open={Boolean(request)}
            onClose={() => setViewSearch(null)}
            closable={false}
            width={600}
            bodyStyle={{ padding: 16 }}
            style={{ padding: 0 }}
        >
            <PageHeader
                title={`Nº ${request?.bookingNumericId}`}
                style={{ padding: '16px 0' }}
                extra={(
                    <Button
                        type="link"
                        onClick={() => setViewSearch(null)}
                    >
                        {t('CLOSE')}
                    </Button>
                )}
            />
            <Spin spinning={loading}>
                {searchDetails.length > 0 ? (
                    <Space direction="vertical" size={24}>
                        <div className="help">
                            <div className="help__tag">
                                <Space size={4}>
                                    <BsLightbulbFill />
                                    {t('HELP_TAG')}
                                </Space>
                            </div>
                            <Text>
                                {t('ORDER_SEARCH_HELP')}
                            </Text>
                        </div>
                        <Collapse
                            ghost
                            defaultActiveKey={['1']}
                        >
                            {groupedByRound.map(({ round, drivers }) => (
                                <Panel
                                    key={round.toString()}
                                    header={renderRoundHeader(round, drivers.length)}
                                >
                                    {drivers.map(renderDriverItem)}
                                </Panel>
                            ))}
                        </Collapse>
                    </Space>
                ) : (
                    <Empty
                        className="order-search__empty"
                        description={t('SEARCH_NO_RESULTS')}
                    />
                )}
            </Spin>
        </Drawer>
    );
};

export { OrderSearch };
